import { useCallback } from 'react';

import { accountsUtils } from 'app-components/flux/accounts';
import { resolveCoaForBusinessChange } from 'app-components/flux/categories/categoriesUtils';
import type { AccountProps } from 'app-components/flux/accounts/accountsTypes';
import type { ChartOfAccount, ChartOfAccountProps } from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import type { CashFlowTransaction } from 'app-components/flux/transactions/transactionsTypes';
import type { TransactionDetailsProjectSelect } from 'app-components/flux/transactions/transactionDetailsForm';

import { type ProjectSelect, useOrnamentType } from 'components/ClientProjectSelect';

type Props = {
  accountsById: Parameters<typeof resolveCoaForBusinessChange>[0]['accountsById'];
  amount: number;
  businessEnabled: boolean;
  businessId: string | null | undefined;
  categoriesById: Parameters<typeof resolveCoaForBusinessChange>[0]['categoriesById'];
  coa: ChartOfAccountProps | null | undefined;
  create: boolean;
  currentTxn: CashFlowTransaction;
  isAssetPayment: boolean;
  setAccountId: (accountId: string) => void;
  setBusinessId: (businessId: string | undefined) => void;
  setCoa: (coa: ChartOfAccount | ChartOfAccountProps) => void;
  setCustomersAndProjectsInternal: (value: TransactionDetailsProjectSelect) => void;
};

export function useBusinessContextSelection({
  accountsById,
  amount,
  businessEnabled,
  businessId,
  categoriesById,
  coa,
  create,
  currentTxn,
  isAssetPayment,
  setAccountId,
  setBusinessId,
  setCoa,
  setCustomersAndProjectsInternal,
}: Props): {
  ornamentType: ReturnType<typeof useOrnamentType>;
  resetCustomersAndProjects: () => void;
  setCustomersAndProjects: (projectSelect: ProjectSelect) => void;
  selectAccount: (selectedAccount: AccountProps | undefined) => void;
} {
  const ornamentType = useOrnamentType({
    transaction: currentTxn,
    coa,
    linkedItem: currentTxn?.linkedItem,
    type: currentTxn?.type,
    amount,
    businessId,
    create,
  });

  const resetCustomersAndProjects = useCallback(() => {
    setCustomersAndProjectsInternal({
      customerId: null,
      projectId: null,
      billable: false,
      payment: isAssetPayment,
    });
  }, [isAssetPayment, setCustomersAndProjectsInternal]);

  const setCustomersAndProjects = useCallback(
    (projectSelect: ProjectSelect) => {
      setCustomersAndProjectsInternal({ ...projectSelect, payment: projectSelect.payment ?? false });
    },
    [setCustomersAndProjectsInternal],
  );

  const selectAccount = useCallback(
    (selectedAccount: AccountProps | undefined) => {
      setAccountId(selectedAccount?.id || '');
      const nextBusinessId = accountsUtils.getBusinessIdForAccountSelection({
        account: selectedAccount,
        businessEnabled,
      });
      if (nextBusinessId !== undefined) {
        setBusinessId(nextBusinessId);

        if (!nextBusinessId) {
          resetCustomersAndProjects();
        }

        const nextCoa = resolveCoaForBusinessChange({
          coa,
          categoriesById,
          accountsById,
          nextBusinessId,
        });
        if (nextCoa) {
          setCoa(nextCoa);
        }
      }
    },
    [
      accountsById,
      businessEnabled,
      categoriesById,
      coa,
      resetCustomersAndProjects,
      setAccountId,
      setBusinessId,
      setCoa,
    ],
  );

  return { ornamentType, resetCustomersAndProjects, setCustomersAndProjects, selectAccount };
}
